import { useEffect, useRef, useState } from "react";
import { ArrowUp, Sparkles, X } from "lucide-react";
import { useSideRail } from "@/state/SideRail";

interface Msg {
  id: string;
  role: "user" | "assistant";
  text: string;
  ts: string;
}

const FALLBACK =
  "I can answer that in the production deployment. For this demo, jump to the Copilot view and use one of the suggested prompts to see a full scripted flow — or browse the entity directly via the sidebar.";

const fmtTime = () =>
  new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

export function SideRail() {
  const { open, contextLabel, suggestions, closeRail } = useSideRail();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-focus the input when the rail opens, and reset the thread per context.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setMessages([]);
      setInput("");
    }
  }, [open, contextLabel]);

  // Scroll to bottom on each new message.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  function send(text: string) {
    if (!text.trim()) return;
    const userMsg: Msg = { id: `u-${Date.now()}`, role: "user", text, ts: fmtTime() };
    setMessages((m) => [...m, userMsg]);
    setTimeout(() => {
      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: "assistant", text: FALLBACK, ts: fmtTime() },
      ]);
    }, 450);
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
    setInput("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e as unknown as React.FormEvent);
    }
  };

  if (!open) return null;

  return (
    <div
      className="border-border bg-canvas pointer-events-auto absolute top-11 right-0 bottom-0 z-40 flex w-[380px] flex-col border-l shadow-xl"
      role="dialog"
      aria-label={`Ask Copilot — ${contextLabel}`}
    >
      <div className="border-border bg-canvas/60 flex shrink-0 items-start justify-between gap-2 border-b px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className="text-foreground inline-flex items-center gap-1.5 text-[12.5px] font-medium">
            <Sparkles className="text-primary h-3.5 w-3.5" /> Ask Copilot
          </div>
          <div className="text-muted-foreground mt-0.5 truncate text-[11.5px]">{contextLabel}</div>
        </div>
        <button
          onClick={closeRail}
          className="text-muted-foreground hover:bg-muted/60 hover:text-foreground rounded-sm p-1 transition-colors"
          aria-label="Close side rail"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <div className="text-muted-foreground text-[12px] leading-relaxed">
            Ask anything about <span className="text-foreground">{contextLabel.toLowerCase()}</span>
            . For full scripted flows, use the main Copilot view.
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m) =>
              m.role === "user" ? (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[88%]">
                    <div className="border-primary/20 bg-primary/8 text-foreground rounded-md border px-2.5 py-1.5 text-[12.5px] leading-snug">
                      {m.text}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-right text-[10.5px]">
                      {m.ts}
                    </div>
                  </div>
                </div>
              ) : (
                <div key={m.id}>
                  <div className="text-muted-foreground mb-0.5 text-[10px] font-medium tracking-wider uppercase">
                    Macro Studio
                  </div>
                  <div className="text-foreground/90 text-[12.5px] leading-snug">{m.text}</div>
                  <div className="text-muted-foreground mt-0.5 text-[10.5px]">{m.ts}</div>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {suggestions.length > 0 && messages.length === 0 && (
        <div className="border-border bg-canvas/40 shrink-0 border-t px-3 py-2">
          <div className="text-muted-foreground mb-1 text-[10px] font-medium tracking-wider uppercase">
            Try
          </div>
          <ul className="space-y-1">
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => send(s.text)}
                  className="border-border bg-card hover:border-primary/40 hover:bg-primary/5 text-foreground w-full rounded-sm border px-2.5 py-1.5 text-left text-[12px] leading-snug transition-colors"
                >
                  {s.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-border bg-canvas shrink-0 border-t px-3 py-2">
        <form
          onSubmit={onSubmit}
          className="border-border bg-card focus-within:border-primary rounded-md border"
        >
          <div className="flex items-end gap-1.5 px-2 py-1.5">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask about this context…"
              className="placeholder:text-muted-foreground max-h-24 min-h-[20px] flex-1 resize-none bg-transparent text-[12.5px] outline-none"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded transition-opacity disabled:opacity-30"
              aria-label="Send"
            >
              <ArrowUp className="h-3 w-3" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
